<template>
  <div class="charge-modal-wrapper">
    <div class="modal-backdrop fade show" v-if="state"></div>
    <div class="modal fade show d-block" tabindex="-1" role="dialog" v-if="state">
      <div class="modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable modal-fullscreen-sm-down charge-dialog" role="document">
        <div class="modal-content shadow-lg border-0">
          <!-- Header -->
          <div class="modal-header border-0 pb-0 pt-3 px-4">
            <div class="d-flex align-items-center w-100">
              <div class="icon-circle me-3" :class="isCashPayment ? 'bg-success-light' : 'bg-primary-light'">
                <AppIcon :name="selectedPaymentIcon" :class="selectedPaymentIcon" :size="28" />
              </div>
              <h5 class="modal-title fw-bold mb-0 text-dark">{{ $t('charge') || 'Charge' }}</h5>
              <button type="button" class="btn-close ms-auto modal-close-button" @click="$emit('close')"></button>
            </div>
          </div>

          <div class="modal-body px-4 pt-2 pb-3">
            <template v-if="!receiptHtml">
              <!-- Total Display Card -->
              <div class="amount-summary-card mb-3">
                <div class="text-center">
                  <div class="label text-uppercase letter-spacing-1 mb-1">{{ $t('total_amount') || 'Total Amount' }}</div>
                  <div class="amount-display">{{ formatMoney(totalAmount) }}</div>
                </div>
              </div>

              <!-- Payment Method Selection -->
              <div class="mb-3">
                <label class="form-label fw-bold mb-2 d-block small">{{ $t('payment_method') || 'Payment Method' }}</label>
                <div class="payment-methods-grid">
                  <div v-for="method in availablePaymentMethods" :key="method.id || method.slug">
                    <button
                      type="button"
                      class="payment-card w-100"
                      :class="{ 'active': paymentMethod === method.slug }"
                      @click="paymentMethod = method.slug"
                    >
                      <AppIcon :name="getPaymentMethodIcon(method.slug)" :class="getPaymentMethodIcon(method.slug)" :size="36" />
                      <span>{{ method.name || method.slug }}</span>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Cash Details -->
              <transition name="fade-slide">
                <div v-if="isCashPayment" class="cash-section mb-3">
                  <div class="card border-0 bg-light p-2 px-3 rounded-4 shadow-sm">
                    <div class="mb-3">
                      <label class="form-label fw-bold small text-muted text-uppercase mb-2">{{ $t('amount_received') || 'Amount Received' }}</label>
                      <div class="input-group input-group-lg border-0 shadow-sm rounded-3 overflow-hidden">
                        <input 
                          type="number" 
                          class="form-control border-0 text-end fs-3 fw-bold bg-white" 
                          v-model.number="amountReceived" 
                          @focus="$event.target.select()"
                        />
                        <span class="input-group-text border-0 bg-white fw-bold text-primary">{{ currencySymbol }}</span>
                      </div>
                    </div>
                    
                    <!-- Quick Buttons -->
                    <div class="quick-amounts-grid mb-3">
                      <button
                        v-for="(amount, index) in quickAmounts"
                        :key="amount"
                        type="button"
                        class="quick-chip"
                        :class="{ active: amountReceived === amount }"
                        @click="setAmount(amount)"
                      >
                        <span v-if="index === 0" class="quick-chip-label">{{ $t('exact') || 'Exact' }}</span>
                        <span>{{ formatQuickAmount(amount) }} {{ currencySymbol }}</span>
                      </button>
                    </div>

                    <!-- Change Display -->
                    <div class="change-display d-flex justify-content-between align-items-center mt-2 p-3 bg-white rounded-3 shadow-sm">
                      <span class="fw-bold text-muted">{{ $t('change') || 'Change' }}</span>
                      <span class="fw-bold fs-4" :class="changeAmount >= 0 ? 'text-success' : 'text-danger'">
                        {{ formatMoney(changeAmount) }}
                      </span>
                    </div>
                  </div>
                </div>
              </transition>

            </template>

            <template v-else>
              <!-- Success View -->
              <div class="success-view py-4">
                <div class="success-icon-wrapper mb-4">
                  <div class="success-icon">
                    <AppIcon name="bi-check-lg" class="bi bi-check-lg" :size="48" />
                  </div>
                </div>
                <h3 class="fw-bold text-center mb-4">{{ $t('order_created_successfully') || 'Order Completed' }}</h3>
                
                <div class="receipt-summary-card">
                  <div class="receipt-item">
                    <span class="label">{{ $t('transaction_id') || 'Transaction ID' }}</span>
                    <button
                      v-if="transactionId"
                      type="button"
                      class="value transaction-reference"
                      @click="openTransactionDetails"
                    >
                      #{{ completedTransactionId }}
                    </button>
                    <span v-else class="value">#{{ completedTransactionId }}</span>
                  </div>
                  <div class="receipt-divider"></div>
                  <div class="receipt-item">
                    <span class="label">{{ $t('payment_method') || 'Payment Method' }}</span>
                    <span class="value">{{ selectedPaymentLabel }}</span>
                  </div>
                  <div class="receipt-divider"></div>
                  <div class="receipt-total">
                    <span class="label">{{ $t('total') || 'Total Paid' }}</span>
                    <span class="value text-primary">{{ formatMoney(totalAmount) }}</span>
                  </div>
                </div>

                <div class="success-notes-card mt-3 text-start">
                  <collapsible-notes-field
                    id="completedTransactionNotes"
                    v-model="localNotes"
                    rows="3"
                    :placeholder="$t('add_notes') || 'Add notes...'"
                  />
                  <button
                    v-if="hasNoteChanges"
                    type="button"
                    class="btn btn-primary w-100 mt-2 save-notes-button"
                    :disabled="savingNotes || !transactionId"
                    @click="saveTransactionNotes"
                  >
                    <span v-if="savingNotes" class="spinner-border spinner-border-sm me-2"></span>
                    <AppIcon v-else name="bi-check-circle" class="bi bi-check-circle me-2" :size="22" />
                    {{ savingNotes ? ($t('saving') || 'Saving...') : ($t('save') || 'Save') }}
                  </button>
                </div>
              </div>
            </template>
          </div>

          <!-- Footer Actions -->
          <div class="modal-footer border-0 p-4 pt-0 pb-4">
            <template v-if="!receiptHtml">
              <div class="row g-2 w-100">
                <div class="col-12 col-md-8">
                  <button 
                    type="button" 
                    class="btn btn-success btn-lg w-100 h-100 py-3 rounded-3 shadow-sm confirm-btn" 
                    @click="confirmCharge"
                    :disabled="isProcessing || !canConfirmPayment"
                  >
                    <span v-if="isProcessing" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    <AppIcon name="bi-shield-check" v-else class="bi bi-shield-check me-2" :size="24" />
                    {{ isProcessing ? ($t('processing') || 'Processing...') : ($t('confirm_payment') || 'Confirm Payment') }}
                  </button>
                </div>
                <div class="col-12 col-md-4">
                  <button type="button" class="btn btn-light btn-lg w-100 h-100 py-3 rounded-3 border" @click="$emit('close')">
                    {{ $t('cancel') || 'Cancel' }}
                  </button>
                </div>
              </div>
            </template>
            <template v-else>
              <div class="row g-2 w-100">
                <div class="col-12 col-md-6">
                  <button type="button" class="btn btn-light btn-lg w-100 h-100 py-3 rounded-3 border" @click="$emit('close')">
                    <AppIcon name="bi-plus-circle" class="bi bi-plus-circle me-2" :size="24" />{{ $t('new_order') || 'New Order' }}
                  </button>
                </div>
                <div class="col-12 col-md-6">
                  <button type="button" class="btn btn-success btn-lg w-100 h-100 py-3 rounded-3 shadow-sm" @click="$emit('print')">
                    <AppIcon name="bi-printer-fill" class="bi bi-printer-fill me-2" :size="24" />{{ $t('print_receipt') || 'Print Receipt' }}
                  </button>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import helper from '../helper';

export default {
  name: 'ChargeModal',
  props: {
    state: { type: Boolean, required: true },
    totalAmount: { type: Number, required: true },
    initialNotes: { type: String, default: '' },
    receiptHtml: { type: String, default: null },
    isProcessing: { type: Boolean, default: false },
    completedTransactionId: { type: String, default: null },
    transactionId: { type: [String, Number], default: null },
    paymentMethods: { type: Array, default: () => [] }
  },
  data() {
    return {
      paymentMethod: '',
      amountReceived: Number(this.totalAmount.toFixed(2)),
      localNotes: this.initialNotes,
      savedNotes: this.initialNotes,
      savingNotes: false
    };
  },
  computed: {
    availablePaymentMethods() {
      return this.paymentMethods.filter(method => method && method.is_active !== false && method.is_active !== 0 && method.slug);
    },
    selectedPaymentMethod() {
      return this.availablePaymentMethods.find(method => method.slug === this.paymentMethod) || null;
    },
    isCashPayment() {
      return this.paymentMethod === 'cash';
    },
    selectedPaymentIcon() {
      return this.getPaymentMethodIcon(this.selectedPaymentMethod?.slug);
    },
    selectedPaymentLabel() {
      return this.selectedPaymentMethod?.name || String(this.paymentMethod || '').replace(/_/g, ' ');
    },
    changeAmount() {
      return (this.amountReceived || 0) - this.totalAmount;
    },
    currencySymbol() {
      return localStorage.getItem('currentCurrency') || '$';
    },
    quickAmounts() {
      const total = Number(this.totalAmount) || 0;
      const amounts = [Number(total.toFixed(2))];
      const roundingSteps = [10, 20, 50, 100, 200, 500, 1000];

      roundingSteps.forEach((step) => {
        const roundedAmount = (Math.floor(total / step) + 1) * step;
        if (roundedAmount > total && !amounts.includes(roundedAmount) && amounts.length < 4) {
          amounts.push(roundedAmount);
        }
      });

      return amounts;
    },
    canConfirmPayment() {
      if (!this.paymentMethod) return false;
      return !this.isCashPayment || Number(this.amountReceived) >= Number(this.totalAmount);
    },
    hasNoteChanges() {
      return this.localNotes !== this.savedNotes;
    }
  },
  watch: {
    state(newVal) {
      if (newVal) {
        this.amountReceived = Number(this.totalAmount.toFixed(2));
        this.localNotes = this.initialNotes;
        this.savedNotes = this.initialNotes;
        this.setDefaultPaymentMethod();
      }
    },
    totalAmount(newVal) {
      if (this.state && this.isCashPayment) {
        this.amountReceived = Number(newVal.toFixed(2));
      }
    },
    paymentMethods: {
      handler() {
        this.setDefaultPaymentMethod();
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    formatMoney(number) {
      return helper.formatMoney(number);
    },
    formatQuickAmount(number) {
      return Number(number).toLocaleString(undefined, { maximumFractionDigits: 2 });
    },
    getPaymentMethodIcon(slug) {
      const icons = {
        cash: 'bi bi-cash-stack',
        credit_card: 'bi bi-credit-card-2-front',
        card: 'bi bi-credit-card-2-front',
        cheque: 'bi bi-journal-check',
        check: 'bi bi-journal-check',
        bank_transfer: 'bi bi-bank',
        transfer: 'bi bi-bank'
      };

      return icons[slug] || 'bi bi-wallet2';
    },
    setDefaultPaymentMethod() {
      if (this.availablePaymentMethods.some(method => method.slug === this.paymentMethod)) return;

      const defaultMethod = this.availablePaymentMethods.find(method => method.is_default)
        || this.availablePaymentMethods[0];
      this.paymentMethod = defaultMethod?.slug || '';
    },
    setAmount(amount) {
      this.amountReceived = Number(amount.toFixed(2));
    },
    async saveTransactionNotes() {
      if (!this.transactionId) return false;
      if (!this.hasNoteChanges) return true;

      try {
        this.savingNotes = true;
        await helper.customPatch(
          `${process.env.VUE_APP_API}/pos/${this.transactionId}`,
          { notes: this.localNotes },
          { showToast: true }
        );
        this.savedNotes = this.localNotes;
        this.$emit('notes-updated', this.localNotes);
        return true;
      } catch (error) {
        window.vueOpener.open({
          message: this.$t('something_went_wrong'),
          type: 'error',
          position: 'bottom'
        });
        return false;
      } finally {
        this.savingNotes = false;
      }
    },
    async openTransactionDetails() {
      const notesSaved = await this.saveTransactionNotes();
      if (!notesSaved) return;

      const transactionId = this.transactionId;
      this.$emit('close');
      this.$router.push({ name: 'PosTransactionDetails', params: { id: transactionId } });
    },
    confirmCharge() {
      this.$emit('confirm', {
        paymentMethod: this.paymentMethod,
        amountReceived: this.amountReceived,
        notes: this.localNotes
      });
    }
  }
}
</script>

<style scoped>
.charge-modal-wrapper { position: relative; }
.modal-backdrop { z-index: 2040; background-color: rgba(0, 0, 0, 0.6); backdrop-filter: blur(4px); }
.modal { z-index: 2050; }

.charge-dialog {
  width: calc(100% - 2rem);
  max-width: 1100px;
}

.charge-dialog .modal-content {
  max-height: calc(100vh - 2rem);
}

.modal-close-button {
  width: 2.75rem;
  height: 2.75rem;
  padding: 0.75rem;
  border-radius: 0.75rem;
}

.icon-circle {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
}

.bg-success-light { background-color: rgba(25, 135, 84, 0.1); color: #198754; }
.bg-primary-light { background-color: rgba(13, 110, 253, 0.1); color: #0d6efd; }

.amount-summary-card {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 16px;
  padding: 1rem;
  border: 1px solid rgba(0,0,0,0.05);
  box-shadow: inset 0 2px 4px rgba(0,0,0,0.02);
}

.amount-summary-card .label {
  color: #6c757d;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 1px;
}

.amount-display {
  font-size: 2rem;
  font-weight: 800;
  color: #0d6efd;
  line-height: 1.2;
}

.payment-card {
  background: white;
  border: 2px solid #f1f3f5;
  border-radius: 14px;
  min-height: 104px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 100%;
}

.payment-methods-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.75rem;
}

.payment-card .app-lucide-icon {
  width: 2.25rem;
  height: 2.25rem;
  margin-bottom: 0.5rem;
  color: #adb5bd;
}

.payment-card span {
  font-weight: 600;
  color: #495057;
}

.payment-card:hover {
  border-color: #dee2e6;
  background-color: #f8f9fa;
  transform: translateY(-2px);
}

.payment-card.active {
  border-color: #0d6efd;
  background-color: rgba(13, 110, 253, 0.05);
}

.payment-card.active .app-lucide-icon {
  color: #0d6efd;
}

.payment-card.active span {
  color: #0d6efd;
}

.quick-amounts-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
}

.quick-chip {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 10px;
  min-height: 56px;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  font-weight: 600;
  color: #495057;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 1.2;
}

.quick-chip:hover {
  border-color: #0d6efd;
  color: #0d6efd;
  background-color: rgba(13, 110, 253, 0.05);
}

.quick-chip.active {
  border-color: #0d6efd;
  color: #0d6efd;
  background-color: rgba(13, 110, 253, 0.08);
  box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.12);
}

.quick-chip-label {
  margin-bottom: 0.2rem;
  font-size: 0.72rem;
  text-transform: uppercase;
  opacity: 0.75;
}

.confirm-btn {
  font-weight: 700;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(25, 135, 84, 0.2);
}

.confirm-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 15px rgba(25, 135, 84, 0.4);
}

.modal-footer .btn {
  min-height: 60px;
  font-size: 1.05rem;
  font-weight: 700;
}

.modal-footer .app-lucide-icon,
.save-notes-button .app-lucide-icon {
  width: 1.4rem;
  height: 1.4rem;
}

/* Success View Styles */
.success-view {
  text-align: center;
}

.success-icon-wrapper {
  display: flex;
  justify-content: center;
}

.success-icon {
  width: 92px;
  height: 92px;
  background-color: #198754;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  box-shadow: 0 10px 20px rgba(25, 135, 84, 0.2);
  animation: scaleIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

@keyframes scaleIn {
  from { transform: scale(0); }
  to { transform: scale(1); }
}

.receipt-summary-card {
  background-color: #f8f9fa;
  border: 2px dashed #dee2e6;
  border-radius: 16px;
  padding: 1.5rem;
  max-width: 400px;
  margin: 0 auto;
}

.transaction-reference {
  padding: 0;
  background: transparent;
  border: 0;
  color: #0d6efd;
  font: inherit;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
}

.transaction-reference:hover {
  text-decoration: underline;
}

.success-notes-card {
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
  padding: 1rem;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 16px;
}

.success-notes-card label .app-lucide-icon {
  width: 1.2rem;
  height: 1.2rem;
}

.save-notes-button {
  min-height: 52px;
  font-weight: 700;
}

.receipt-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}

.receipt-item .label {
  color: #6c757d;
  font-size: 0.9rem;
}

.receipt-item .value {
  font-weight: 600;
  color: #212529;
}

.receipt-divider {
  height: 1px;
  border-top: 1px dashed #dee2e6;
  margin: 0.75rem 0;
}

.receipt-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.5rem;
}

.receipt-total .label {
  font-weight: 700;
  font-size: 1rem;
}

.receipt-total .value {
  font-size: 1.5rem;
  font-weight: 800;
}

/* Transitions */
.fade-slide-enter-active, .fade-slide-leave-active {
  transition: all 0.3s ease;
}
.fade-slide-enter, .fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.letter-spacing-1 { letter-spacing: 1px; }

@media (max-width: 576px) {
  .charge-dialog {
    width: 100%;
    max-width: none;
  }

  .amount-display { font-size: 2rem; }
  .modal-body { padding-left: 1rem; padding-right: 1rem; }

  .quick-amounts-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
